/**
 * Cloche de la barre haute (08/10/2026 ; refonte v4 du 18/10/2026) : la
 * pastille donne le nombre EXACT d'avis non lus (lu sur le serveur, sans
 * plafond) ; un appui ouvre les plus récents sans changer de page — feuille
 * depuis le bas au téléphone, panneau ancré sur ordinateur. On y ouvre l'élément
 * concerné, on marque un avis lu, ou tout jusqu'au plus récent affiché ;
 * « Voir toutes les notifications » mène à la boîte de réception.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { CursorPage } from '@myequip/shared';
import { AlertTriangle, Bell, Check, CheckCheck } from 'lucide-react';
import { notificationEventLabel } from '@myequip/shared';
import { api } from '../../core/api.js';
import { useAuth } from '../../core/auth.js';
import { dateHeureFr } from '../../core/format.js';
import { t } from '../../core/i18n.js';
import { useBreakpoint } from '../../design/tokens.js';
import { Button } from '../../components/ui.js';
import { ButtonLink } from '../../components/ui/action.js';
import { Banner } from '../../components/ui/Feedback.js';
import { Sheet, SheetPanel } from '../../components/ui/Sheet.js';
import { RecordIcon, RecordList, RecordRow } from '../../components/RecordRow.js';
import {
  INBOX_PATH, openNotification, PUSH_OPEN_PATH, useMarkNotification, useReadAll, useUnreadCount, type InboxNotification,
} from './notification-data.js';
import { OPEN_FROM_PUSH } from '../../core/push.js';
import './notifications.css';

/** Nombre d'avis montrés dans le panneau ; le reste vit dans la boîte de réception. */
const PANEL_SIZE = 6;

export function NotificationBell(): ReactNode {
  const { me } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const wide = useBreakpoint('md');
  const wrapper = useRef<HTMLSpanElement>(null);
  const [open, setOpen] = useState(false);
  const [failure, setFailure] = useState<string>();
  const counts = useUnreadCount();
  const unread = counts.data?.unread ?? 0;
  const latest = useQuery({
    queryKey: ['notifications', me?.id, 'cloche'],
    queryFn: () => api<CursorPage<InboxNotification>>(`${INBOX_PATH}?paged=1&unreadOnly=true&limit=${PANEL_SIZE}`),
    enabled: open,
    staleTime: 0,
    retry: false,
  });
  const mark = useMarkNotification();
  const readAll = useReadAll();

  // Toute navigation referme le panneau : il ne survit pas à la page.
  useEffect(() => {
    setOpen(false);
  }, [location.pathname, location.search]);

  // Notification push touchée alors que MyEquip est ouvert : le service worker
  // demande la page d'ouverture, sans recharger ce qui est en cours.
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return undefined;
    const onMessage = (event: MessageEvent): void => {
      const data = event.data as { type?: unknown; url?: unknown } | null;
      if (data?.type === OPEN_FROM_PUSH && typeof data.url === 'string' && data.url.startsWith(`${PUSH_OPEN_PATH}#`)) {
        navigate(data.url);
      }
    };
    navigator.serviceWorker.addEventListener('message', onMessage);
    return () => navigator.serviceWorker.removeEventListener('message', onMessage);
  }, [navigate]);

  // Panneau ancré (ordinateur) : Échap et un appui à l'extérieur le referment.
  useEffect(() => {
    if (!open || !wide) return undefined;
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') return;
      setOpen(false);
      wrapper.current?.querySelector<HTMLButtonElement>('button[aria-haspopup]')?.focus();
    };
    const onPointerDown = (event: PointerEvent): void => {
      if (!wrapper.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('pointerdown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [open, wide]);

  const openOne = (notification: InboxNotification): void => {
    setFailure(undefined);
    openNotification(notification, queryClient)
      .then((target) => {
        setOpen(false);
        navigate(target ?? INBOX_PATH);
      })
      .catch((error: unknown) => setFailure(error instanceof Error ? error.message : t('notifications.ouvrirImpossible')));
  };

  const items = latest.data?.items ?? [];
  // Repère : l'avis non lu le plus récent que le panneau affiche, jamais un avis
  // arrivé depuis (le compteur se relit en arrière-plan).
  const upTo = items[0]?.id;
  const error = failure ?? ((mark.error ?? readAll.error) as Error | null)?.message;
  const body = (
    <div className="notif-panel">
      {error ? <Banner tone="danger">{error}</Banner> : null}
      {latest.isPending ? (
        <p className="notif-panel__state" role="status">{t('commun.chargement')}</p>
      ) : latest.isError ? (
        // Jamais « à jour » quand la liste n'a pas pu être lue.
        <Banner tone="danger" action={{ label: t('notifications.reessayer'), onClick: () => void latest.refetch() }}>
          {t('notifications.chargementImpossible')}
        </Banner>
      ) : items.length === 0 ? (
        <p className="notif-panel__state">{t('notifications.aJour')}</p>
      ) : (
        <RecordList label={t('notifications.nonLuesListe')}>
          {items.map((notification) => (
            <li key={notification.id}>
              <RecordRow
                onOpen={() => openOne(notification)}
                lead={<RecordIcon>{notification.critical ? <AlertTriangle size={20} /> : <Bell size={20} />}</RecordIcon>}
                title={notification.title}
                lines={[`${notificationEventLabel(notification.event)} · ${dateHeureFr(notification.created_at)}`]}
                trailing={(
                  <Button
                    variant="ghost"
                    icon
                    disabled={mark.isPending && mark.variables?.id === notification.id}
                    ariaLabel={t('notifications.marquerLueDe', { titre: notification.title })}
                    onClick={() => {
                      setFailure(undefined);
                      mark.mutate({ id: notification.id, read: true });
                    }}
                  >
                    <Check size={18} aria-hidden="true" />
                  </Button>
                )}
              />
            </li>
          ))}
        </RecordList>
      )}
    </div>
  );
  const footer = (
    <>
      {unread > 0 || readAll.isPending ? (
        <Button
          variant="secondary"
          disabled={!upTo || readAll.isPending}
          onClick={() => {
            setFailure(undefined);
            if (upTo) readAll.mutate(upTo);
          }}
        >
          <CheckCheck size={18} aria-hidden="true" />
          {readAll.isPending ? t('notifications.toutLireEnCours') : t('notifications.toutLire')}
        </Button>
      ) : null}
      <ButtonLink to={INBOX_PATH} variant="ghost">
        {unread > PANEL_SIZE ? t('notifications.voirNonLues', { n: unread }) : t('notifications.voirToutes')}
      </ButtonLink>
    </>
  );
  const panel = {
    title: t('notifications.titre'),
    ...(unread > 0 ? { description: t('notifications.nonLuesNombre', { n: unread }) } : {}),
    children: body,
    footer,
  };

  return (
    <span ref={wrapper} className="notif-bell">
      <Button
        variant="ghost"
        icon
        ariaLabel={unread > 0 ? t('notifications.clocheNonLues', { n: unread }) : t('notifications.titre')}
        ariaHasPopup="dialog"
        ariaExpanded={open}
        onClick={() => {
          setFailure(undefined);
          setOpen((current) => !current);
        }}
      >
        <Bell size={20} aria-hidden="true" />
        {unread > 0 ? <span className="nav-badge" aria-hidden="true">{unread > 99 ? '99+' : unread}</span> : null}
      </Button>
      {open && wide ? (
        <div className="notif-popover" role="dialog" aria-label={t('notifications.titre')}>
          <SheetPanel {...panel} />
        </div>
      ) : null}
      {open && !wide ? <Sheet {...panel} swipeDismiss onClose={() => setOpen(false)} /> : null}
    </span>
  );
}
